import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import {
  QrCode, Package as PackageIcon, Printer, UserPlus, ChatCircleDots,
  FileText, Scan, Plus, ArrowsClockwise, Download, WhatsappLogo, Trash,
  Database,
} from "@phosphor-icons/react";

const TABS = ["generate", "batches", "print", "assign", "requests", "challans", "tracking", "audit"];
const TAB_LABELS = {
  generate: "Generate", batches: "Batches", print: "Print Center",
  assign: "Customer Assign", requests: "Bag Requests",
  challans: "Challan / E-Bill", tracking: "Tracking",
};

export default function QrInventory() {
  const [tab, setTab] = useState("batches");
  const [inventory, setInventory] = useState(null);
  const [assignPrefill, setAssignPrefill] = useState(null);

  const loadInv = () => api.get("/qr-inventory").then(r => setInventory(r.data));
  useEffect(() => { loadInv(); }, []);

  const handleAssignFromRequest = (req) => {
    // Jump to Assign tab pre-filled with the request's customer + count
    // and remember the source_bag_request_id so we can close the loop.
    setAssignPrefill({
      customer_id: req.customer_id,
      customer_name: req.customer_name,
      count: req.quantity,
      waste_category: req.waste_category,
      colour_code: req.colour_code,
      source_bag_request_id: req.id,
    });
    setTab("assign");
  };

  return (
    <AppShell title="QR Bag & Inventory" subtitle="Module 9"
      actions={
        <button onClick={loadInv} data-testid="qr-refresh"
                className="h-10 px-3 border border-[#E5E2D9] rounded text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
          <ArrowsClockwise size={12}/>Refresh
        </button>
      }>

      {/* Inventory KPIs */}
      {inventory && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="qr-kpis">
          <Kpi label="Total" value={inventory.total} tone="#1A1A1A"/>
          <Kpi label="Available" value={inventory.by_status?.available} tone="#2D5A27"/>
          <Kpi label="Assigned" value={inventory.by_status?.assigned} tone="#7A5A11"/>
          <Kpi label="Delivered" value={inventory.by_status?.delivered} tone="#0E7490"/>
          <Kpi label="Collected" value={inventory.by_status?.collected} tone="#5C5A55"/>
          <Kpi label="Missing" value={inventory.by_status?.missing} tone="#9F1239"/>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E5E2D9] overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`qr-tab-${t}`}
                  className={`px-5 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-[#2D5A27] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A55]"}`}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === "generate" && <GenerateTab onDone={() => { loadInv(); setTab("batches"); }}/>}
      {tab === "batches" && <BatchesTab/>}
      {tab === "print" && <PrintTab/>}
      {tab === "assign" && <AssignTab onDone={loadInv}
                                        gotoChallans={() => setTab("challans")}
                                        prefill={assignPrefill}
                                        onPrefillConsumed={() => setAssignPrefill(null)}/>}
      {tab === "requests" && <RequestsTab onAssign={handleAssignFromRequest}/>}
      {tab === "challans" && <ChallansTab onDone={loadInv}/>}
      {tab === "tracking" && <TrackingTab onDone={loadInv}/>}
      {tab === "audit" && <AuditTab/>}
    </AppShell>
  );
}

const Kpi = ({ label, value, tone }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md px-4 py-3">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">{label}</div>
    <div className="text-2xl font-semibold mt-0.5 font-mono" style={{ color: tone }}>{value ?? 0}</div>
  </div>
);

/* ---------- 1. Generate ---------- */
const BMW_COLOURS = [
  { v: "yellow", label: "Yellow", swatch: "#F5C518" },
  { v: "blue",   label: "Blue",   swatch: "#1E5FA0" },
  { v: "red",    label: "Red",    swatch: "#C0392B" },
  { v: "white",  label: "White",  swatch: "#F4F4F4" },
];
const GENERAL_COLOURS = [
  { v: "black",  label: "Black",  swatch: "#1A1A1A" },
  { v: "grey",   label: "Grey",   swatch: "#7A7A7A" },
];

function GenerateTab({ onDone }) {
  const [f, setF] = useState({
    waste_category: "BMW", colour_code: "yellow",
    prefix: "RB", start_number: 1, count: 100,
    price_per_bag: 18, batch_label: "",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const palette = f.waste_category === "BMW" ? BMW_COLOURS : GENERAL_COLOURS;
  useEffect(() => {
    // Whenever category flips, snap colour to the first valid one for that palette.
    if (!palette.some(c => c.v === f.colour_code)) {
      setF(s => ({ ...s, colour_code: palette[0].v }));
    }
  }, [f.waste_category, palette, f.colour_code]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/qr-batches", f);
      setPreview(r.data);
      toast.success(`Batch created: ${r.data.first_code} → ${r.data.last_code}`);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const cleanPrefix = (f.prefix || "RB").replace(/[^A-Z0-9]/gi, "").toUpperCase() || "RB";
  const endNum = Number(f.start_number) + Number(f.count) - 1;
  const pad = (n) => String(n).padStart(6, "0");
  return (
    <form onSubmit={submit} className="max-w-xl bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4" data-testid="qr-gen-form">
      <Field label="Waste Category">
        <div className="grid grid-cols-2 gap-2">
          {[{v:"BMW",l:"Biomedical Waste (BMW)"},{v:"GENERAL",l:"General Waste"}].map(o => (
            <button key={o.v} type="button" onClick={() => setF(s => ({...s, waste_category: o.v}))}
                    data-testid={`qr-gen-cat-${o.v.toLowerCase()}`}
                    className={`h-10 px-3 border rounded text-sm transition-colors ${
                      f.waste_category === o.v
                        ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                        : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
              {o.l}
            </button>
          ))}
        </div>
      </Field>
      <Field label={`Bag Colour · CPCB coding for ${f.waste_category === "BMW" ? "Biomedical" : "General"}`}>
        <div className="flex flex-wrap gap-2">
          {palette.map(c => (
            <button key={c.v} type="button" onClick={() => setF(s => ({...s, colour_code: c.v}))}
                    data-testid={`qr-gen-colour-${c.v}`}
                    className={`h-10 px-3 pr-4 inline-flex items-center gap-2 border rounded text-sm transition-colors ${
                      f.colour_code === c.v
                        ? "border-[#2D5A27] ring-2 ring-[#2D5A27]/30"
                        : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
              <span className="w-4 h-4 rounded-full border border-[#00000020]" style={{ background: c.swatch }}/>
              {c.label}
            </button>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="QR Prefix">
          <input value={f.prefix} onChange={e => setF(s => ({...s, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")}))}
                 required maxLength="8" data-testid="qr-gen-prefix" placeholder="RB"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono uppercase"/>
        </Field>
        <Field label="QR Start Number">
          <input type="number" min="1" required value={f.start_number} onChange={e => setF(s => ({...s, start_number: Number(e.target.value)}))}
                 data-testid="qr-gen-start" className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono"/>
        </Field>
        <Field label="QR Count">
          <input type="number" min="1" max="10000" required value={f.count} onChange={e => setF(s => ({...s, count: Number(e.target.value)}))}
                 data-testid="qr-gen-count" className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono"/>
        </Field>
      </div>
      <Field label="Price per Bag (₹)">
        <input type="number" min="0" step="0.01" required value={f.price_per_bag} onChange={e => setF(s => ({...s, price_per_bag: Number(e.target.value)}))}
               data-testid="qr-gen-price" className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono"/>
      </Field>
      <Field label="Batch Label (optional)">
        <input value={f.batch_label} onChange={e => setF(s => ({...s, batch_label: e.target.value}))}
               data-testid="qr-gen-label" placeholder="e.g. April 2026 BMW Yellow"
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm"/>
      </Field>
      <div className="bg-[#F7F6F2] rounded p-3 text-xs font-mono">
        Will generate: <span className="text-[#2D5A27] font-semibold">{cleanPrefix}{pad(f.start_number)}</span> → <span className="text-[#2D5A27] font-semibold">{cleanPrefix}{pad(endNum)}</span>
        <span className="ml-2">Total cost: ₹{(f.count * f.price_per_bag).toFixed(2)}</span>
      </div>
      <button type="submit" disabled={busy} data-testid="qr-gen-submit"
              className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded text-sm font-medium inline-flex items-center gap-2">
        <Plus size={14}/>Generate Batch
      </button>
      {preview && (
        <div className="bg-[#EFF3EE] border border-[#D4E0D1] rounded p-3 text-xs" data-testid="qr-gen-result">
          ✓ <span className="font-semibold">{preview.label}</span> created — {preview.first_code} → {preview.last_code} ({preview.count} bags)
        </div>
      )}
    </form>
  );
}

/* ---------- 2. Batches ---------- */
function BatchesTab() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = () =>
    api.get("/qr-batches").then(r => {
      setRows(r.data || []);
      setSelected(new Set());
    });
  useEffect(() => { load(); }, []);

  // A batch is "non-active" iff every bag in it is still `generated`
  // (no assign / deliver / collect / missing has ever happened). We
  // derive this from the aggregated `status_counts` the list endpoint
  // already returns — no extra call.
  const isDeletable = (b) => {
    const sc = b.status_counts || {};
    // Only `generated` may be present (or the batch may have no bags
    // at all, in which case status_counts is empty).
    const keys = Object.keys(sc);
    if (keys.length === 0) return true;
    return keys.every(k => k === "generated");
  };

  const deletableRows  = rows.filter(isDeletable);
  const allSelected = deletableRows.length > 0 && deletableRows.every(b => selected.has(b.id));

  const toggle = (id) => setSelected(prev => {
    const nx = new Set(prev);
    nx.has(id) ? nx.delete(id) : nx.add(id);
    return nx;
  });
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(deletableRows.map(b => b.id)));
  };

  const del = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/qr-batches/bulk-delete", {
        batch_ids: Array.from(selected),
      });
      const d = r.data?.deleted_count ?? 0;
      const s = r.data?.skipped_count ?? 0;
      if (d > 0) toast.success(`Deleted ${d} batch${d > 1 ? "es" : ""}`);
      if (s > 0) {
        const active = (r.data?.skipped || []).filter(x => x.reason === "active_bags");
        toast.error(
          active.length > 0
            ? `${s} batch${s > 1 ? "es" : ""} protected (active bags in use)`
            : `${s} batch${s > 1 ? "es" : ""} skipped`,
        );
      }
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to delete batches");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {isSuper && (
        <div className="flex items-center gap-2" data-testid="qr-batches-toolbar">
          <div className="text-[11px] text-[#5C5A55]">
            {selected.size > 0
              ? <span data-testid="qr-batches-selected-count"><b>{selected.size}</b> selected</span>
              : <span className="text-[#8A8780]">Select non-active batches to delete. Batches with assigned or delivered bags are protected.</span>}
          </div>
          <div className="flex-1"/>
          <button data-testid="qr-batches-delete-selected" type="button"
                  disabled={selected.size === 0 || busy}
                  onClick={() => setConfirmOpen(true)}
                  className="inline-flex items-center gap-2 h-9 px-3 border border-[#C25D38] text-[#C25D38] rounded-md text-xs hover:bg-[#F9EDE7] disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash size={12} weight="bold"/>Delete Selected ({selected.size})
          </button>
        </div>
      )}

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <table className="min-w-full text-sm" data-testid="qr-batches-table">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
            <tr>
              {isSuper && (
                <th className="px-3 py-2.5 text-left w-8">
                  <input type="checkbox" checked={allSelected}
                         onChange={toggleAll}
                         disabled={deletableRows.length === 0}
                         data-testid="qr-batches-select-all"
                         title={deletableRows.length === 0
                           ? "No deletable batches"
                           : "Select all non-active batches"}/>
                </th>
              )}
              <th className="px-4 py-2.5 text-left">Label</th>
              <th className="px-4 py-2.5 text-left">Category</th>
              <th className="px-4 py-2.5 text-left">Range</th>
              <th className="px-4 py-2.5 text-right">Count</th>
              <th className="px-4 py-2.5 text-right">₹/bag</th>
              <th className="px-4 py-2.5 text-left">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F7F6F2]">
            {rows.length === 0 && <tr><td colSpan={isSuper ? 8 : 7} className="px-4 py-6 text-center text-[#8A8780]">No batches yet</td></tr>}
            {rows.map(b => {
              const deletable = isDeletable(b);
              return (
                <tr key={b.id} data-testid={`qr-batch-${b.id}`}>
                  {isSuper && (
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(b.id)}
                             onChange={() => toggle(b.id)}
                             disabled={!deletable}
                             data-testid={`qr-batch-select-${b.id}`}
                             title={deletable ? "Select for deletion" : "Protected — bags are in use"}/>
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium">
                    {b.label}
                    {!deletable && isSuper && (
                      <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-[#F9E4B7] text-[#7A5A11] rounded uppercase tracking-wider"
                            title="Some bags are already assigned / delivered / collected.">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3"><span className="text-[10px] px-2 py-0.5 bg-[#D4E0D1] text-[#2D5A27] rounded">{b.waste_category}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{b.first_code} → {b.last_code}</td>
                  <td className="px-4 py-3 text-right font-mono">{b.count}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{b.price_per_bag}</td>
                  <td className="px-4 py-3 text-xs">
                    {Object.entries(b.status_counts || {}).map(([k,v]) => (
                      <span key={k} className="mr-2 text-[#5C5A55]">{k}:<b className="text-[#1A1A1A]">{v}</b></span>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a href={`${API_BASE}/qr-batches/${b.id}/print.pdf?paper=A4`} target="_blank" rel="noreferrer"
                       data-testid={`qr-print-${b.id}`} className="text-[#2D5A27] text-xs inline-flex items-center gap-1">
                      <Download size={12}/>Print
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="qr-batches-delete-modal"
             onClick={() => !busy && setConfirmOpen(false)}>
          <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Trash size={18} className="text-[#C25D38]"/>
              <div className="text-base font-semibold">Delete {selected.size} batch{selected.size > 1 ? "es" : ""}?</div>
            </div>
            <p className="text-sm text-[#5C5A55]">
              Only batches whose bags have never been assigned will be
              removed. Any batch with active bags will be skipped
              automatically. This cannot be undone.
            </p>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirmOpen(false)} disabled={busy}
                      data-testid="qr-batches-delete-cancel"
                      className="flex-1 h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
                Cancel
              </button>
              <button type="button" onClick={del} disabled={busy}
                      data-testid="qr-batches-delete-confirm"
                      className="flex-1 h-10 px-4 bg-[#C25D38] hover:bg-[#A74A29] text-white rounded-md text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1">
                {busy ? "Deleting…" : (<><Trash size={12} weight="bold"/> Delete</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- 3. Print Center ---------- */
function PrintTab() {
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [paper, setPaper] = useState("A4");
  useEffect(() => { api.get("/qr-batches").then(r => setBatches(r.data || [])); }, []);
  const print = () => {
    if (!batchId) { toast.error("Pick a batch"); return; }
    const token = localStorage.getItem("rescigo_token");
    fetch(`${API_BASE}/qr-batches/${batchId}/print.pdf?paper=${paper}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }).catch(() => toast.error("Print failed"));
  };
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-5 max-w-2xl space-y-4" data-testid="qr-print-tab">
      <Field label="Batch">
        <select value={batchId} onChange={e => setBatchId(e.target.value)} data-testid="qr-print-batch"
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm">
          <option value="">— Select a batch —</option>
          {batches.map(b => (
            <option key={b.id} value={b.id}>{b.label} ({b.first_code}–{b.last_code}, {b.count} bags)</option>
          ))}
        </select>
      </Field>
      <Field label="Paper Size">
        <div className="flex gap-2">
          {["A3", "A4", "sticker"].map(p => (
            <button key={p} type="button" onClick={() => setPaper(p)} data-testid={`qr-paper-${p}`}
                    className={`h-9 px-4 border rounded text-xs uppercase tracking-wider transition-colors ${paper === p ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
              {p}
            </button>
          ))}
        </div>
      </Field>
      <button type="button" onClick={print} disabled={!batchId} data-testid="qr-print-go"
              className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded text-sm font-medium inline-flex items-center gap-2">
        <Printer size={14}/>Generate PDF
      </button>
    </div>
  );
}

/* ---------- 4. Assign ---------- */
// Iter 62 — Stepped assign flow:
//   1. Type-to-search customer   →
//   2. Pick waste category       →
//   3. Pick colour code          →
//   4. Only matching batches surface (with stock) →
//   5. Count + submit.
function AssignTab({ onDone, gotoChallans, prefill, onPrefillConsumed }) {
  const [batches, setBatches] = useState([]);
  const [customerQ, setCustomerQ] = useState("");
  const [customerHits, setCustomerHits] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({
    waste_category: "BMW", colour_code: "yellow",
    batch_id: "", count: 10,
    source_bag_request_id: null,
  });
  const [result, setResult] = useState(null);
  const palette = f.waste_category === "BMW" ? BMW_COLOURS : GENERAL_COLOURS;

  // Load all batches once (they're small: <500 usually) — filter client-side.
  useEffect(() => { api.get("/qr-batches").then(r => setBatches(r.data || [])); }, []);

  // Reset colour whenever category flips.
  useEffect(() => {
    if (!palette.some(c => c.v === f.colour_code)) {
      setF(s => ({ ...s, colour_code: palette[0].v, batch_id: "" }));
    } else {
      setF(s => ({ ...s, batch_id: "" }));
    }
  }, [f.waste_category, palette, f.colour_code]);

  // Type-to-search customer (debounced).
  useEffect(() => {
    if (customer) return;
    const q = customerQ.trim();
    if (q.length < 2) { setCustomerHits([]); return; }
    const t = setTimeout(() => {
      api.get(`/customers?q=${encodeURIComponent(q)}`)
        .then(r => setCustomerHits((r.data || []).slice(0, 15)))
        .catch(() => setCustomerHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [customerQ, customer]);

  const pickCustomer = (c) => {
    setCustomer(c);
    setCustomerQ("");
    setCustomerHits([]);
  };
  const clearCustomer = () => {
    setCustomer(null);
    setF(s => ({ ...s, batch_id: "" }));
  };

  // Consume any incoming prefill (from RequestsTab → Assign flow).
  useEffect(() => {
    if (!prefill) return;
    (async () => {
      // Fetch the specific customer so the picker shows their card.
      try {
        const r = await api.get(`/customers/${prefill.customer_id}`);
        if (r.data) setCustomer(r.data);
      } catch { /* silent */ }
      setF(s => ({
        ...s,
        waste_category: prefill.waste_category || s.waste_category,
        colour_code: prefill.colour_code || s.colour_code,
        count: prefill.count || s.count,
        source_bag_request_id: prefill.source_bag_request_id || null,
      }));
      toast.info(`Fulfilling bag request from ${prefill.customer_name}`);
    })();
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  // Batches matching the current category + colour that still have stock.
  const eligibleBatches = batches
    .filter(b => (b.available || 0) > 0)
    .filter(b => (b.waste_category || "").toUpperCase() === f.waste_category)
    .filter(b => !b.colour_code || b.colour_code === f.colour_code);

  const batch = batches.find(b => b.id === f.batch_id);
  const cleanPrefix = batch?.prefix || "RB";
  const pad = (n) => String(n).padStart(6, "0");
  const startNum = batch?.next_available_start || batch?.start_number || 1;
  const endNum = Number(startNum) + Number(f.count) - 1;
  const cost = batch ? (Number(f.count) * Number(batch.price_per_bag)).toFixed(2) : "0.00";

  const canSubmit = customer && f.batch_id && Number(f.count) > 0
                     && batch && Number(f.count) <= (batch.available || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const r = await api.post("/qr-bags/assign-range", {
        customer_id: customer.id,
        batch_id: f.batch_id,
        start_number: startNum,
        count: f.count,
        source_bag_request_id: f.source_bag_request_id,
      });
      setResult(r.data);
      toast.success(`Assigned ${r.data.assigned_count} bags · Challan ${r.data.challan_no} auto-created`);
      api.get("/qr-batches").then(rr => setBatches(rr.data || []));
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const colourSwatch = (v) => ({
    yellow: "#F5C518", blue: "#1E5FA0", red: "#C0392B", white: "#F4F4F4",
    black: "#1A1A1A", grey: "#7A7A7A",
  }[v] || "#7A7A7A");

  return (
    <form onSubmit={submit} className="max-w-2xl bg-white border border-[#E5E2D9] rounded-md p-5 space-y-5" data-testid="qr-assign-form">
      {/* Step 1 · Customer */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-5 h-5 rounded-full bg-[#2D5A27] text-white text-[10px] font-bold flex items-center justify-center">1</span>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">Customer</label>
        </div>
        {customer ? (
          <div className="flex items-center justify-between bg-[#EFF3EE] border border-[#D4E0D1] rounded p-3" data-testid="qr-assign-cust-picked">
            <div>
              <div className="font-semibold text-sm">{customer.name}</div>
              <div className="text-[11px] text-[#5C5A55] font-mono">{customer.customer_code} · {customer.phone}</div>
            </div>
            <button type="button" onClick={clearCustomer} data-testid="qr-assign-cust-clear"
                    className="text-[#5C5A55] hover:text-[#9F1239] text-xs underline">Change</button>
          </div>
        ) : (
          <div className="relative">
            <input value={customerQ} onChange={e => setCustomerQ(e.target.value)}
                   placeholder="Type name, phone or customer code…"
                   data-testid="qr-assign-cust-search" autoComplete="off"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm"/>
            {customerHits.length > 0 && (
              <div className="absolute z-10 top-11 left-0 right-0 bg-white border border-[#E5E2D9] rounded shadow-md max-h-60 overflow-y-auto">
                {customerHits.map(c => (
                  <button type="button" key={c.id} onClick={() => pickCustomer(c)}
                          data-testid={`qr-assign-cust-hit-${c.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-[#F7F6F2] border-b border-[#F7F6F2] last:border-b-0">
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-[10px] text-[#5C5A55] font-mono">{c.customer_code} · {c.phone}</div>
                  </button>
                ))}
              </div>
            )}
            {customerQ.length >= 2 && customerHits.length === 0 && (
              <div className="text-[10px] text-[#8A8780] mt-1">No matching customer</div>
            )}
          </div>
        )}
      </div>

      {/* Step 2 · Waste category */}
      {customer && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-[#2D5A27] text-white text-[10px] font-bold flex items-center justify-center">2</span>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">Waste Category</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[{v:"BMW",l:"Biomedical Waste (BMW)"},{v:"GENERAL",l:"General Waste"}].map(o => (
              <button key={o.v} type="button" onClick={() => setF(s => ({...s, waste_category: o.v}))}
                      data-testid={`qr-assign-cat-${o.v.toLowerCase()}`}
                      className={`h-10 px-3 border rounded text-sm transition-colors ${
                        f.waste_category === o.v
                          ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                          : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3 · Colour code */}
      {customer && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-[#2D5A27] text-white text-[10px] font-bold flex items-center justify-center">3</span>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">Bag Colour · CPCB coding</label>
          </div>
          <div className="flex flex-wrap gap-2">
            {palette.map(c => (
              <button key={c.v} type="button" onClick={() => setF(s => ({...s, colour_code: c.v}))}
                      data-testid={`qr-assign-colour-${c.v}`}
                      className={`h-10 px-3 pr-4 inline-flex items-center gap-2 border rounded text-sm transition-colors ${
                        f.colour_code === c.v
                          ? "border-[#2D5A27] ring-2 ring-[#2D5A27]/30"
                          : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                <span className="w-4 h-4 rounded-full border border-[#00000020]" style={{ background: colourSwatch(c.v) }}/>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4 · Batch — only matching, only in-stock */}
      {customer && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-[#2D5A27] text-white text-[10px] font-bold flex items-center justify-center">4</span>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">
              Batch · {eligibleBatches.length} match{eligibleBatches.length !== 1 ? "es" : ""} in stock
            </label>
          </div>
          {eligibleBatches.length === 0 ? (
            <div className="bg-[#FFF7D6] border border-[#F5C518]/60 text-[#7A5A11] rounded p-3 text-xs">
              No batches available for <b>{f.waste_category}</b> / <b>{f.colour_code}</b>. Generate a batch first.
            </div>
          ) : (
            <div className="space-y-2">
              {eligibleBatches.map(b => (
                <button key={b.id} type="button" onClick={() => setF(s => ({...s, batch_id: b.id}))}
                        data-testid={`qr-assign-batch-${b.id}`}
                        className={`w-full text-left border rounded p-3 flex items-center justify-between gap-3 transition-colors ${
                          f.batch_id === b.id
                            ? "border-[#2D5A27] ring-2 ring-[#2D5A27]/30 bg-[#EFF3EE]"
                            : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{b.label}</div>
                    <div className="text-[10px] text-[#5C5A55] font-mono">
                      {b.prefix} · next {b.next_available_code || "—"}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[11px] text-[#5C5A55]">₹{b.price_per_bag}/bag</div>
                    <div className="text-[11px] font-mono text-[#2D5A27]">{b.available} available</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 5 · Count + preview + submit */}
      {customer && batch && (
        <div className="space-y-3 pt-2 border-t border-[#E5E2D9]">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-5 h-5 rounded-full bg-[#2D5A27] text-white text-[10px] font-bold flex items-center justify-center">5</span>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">Count</label>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[10, 20, 50, 100].map(q => (
              <button key={q} type="button" onClick={() => setF(s => ({...s, count: q}))}
                      data-testid={`qr-assign-qty-${q}`}
                      disabled={q > (batch.available || 0)}
                      className={`h-10 border rounded text-sm font-mono transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        f.count === q
                          ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                          : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                {q}
              </button>
            ))}
          </div>
          <input type="number" min="1" max={batch.available} required value={f.count}
                 onChange={e => setF(s => ({...s, count: Number(e.target.value)}))}
                 data-testid="qr-assign-count"
                 className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono"/>
          <div className="bg-[#F7F6F2] rounded p-3 text-xs font-mono">
            Range: <span className="text-[#2D5A27]">{cleanPrefix}{pad(startNum)}</span> → <span className="text-[#2D5A27]">{cleanPrefix}{pad(endNum)}</span>
            <span className="ml-3">Total: <span className="text-[#2D5A27] font-bold">₹{cost}</span></span>
          </div>
          <button type="submit" disabled={!canSubmit} data-testid="qr-assign-submit"
                  className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-40 text-white rounded text-sm font-medium inline-flex items-center gap-2">
            <UserPlus size={14}/>Assign + Auto-Create Challan
          </button>
        </div>
      )}

      {result && (
        <div className="bg-[#EFF3EE] border border-[#D4E0D1] rounded p-3 text-xs space-y-1.5" data-testid="qr-assign-result">
          <div>✓ Assigned <b>{result.assigned_count}</b> bags ({result.first_code}–{result.last_code}) to <b>{result.customer_name}</b> · ₹{result.total_cost}</div>
          <div className="flex items-center gap-2">
            <FileText size={11}/>
            <span>Draft challan <span className="font-mono font-semibold">{result.challan_no}</span> auto-created.</span>
            <button type="button" onClick={gotoChallans} data-testid="qr-assign-goto"
                    className="text-[#2D5A27] underline">Go to Challans →</button>
          </div>
        </div>
      )}
    </form>
  );
}

/* ---------- 5. Bag Requests ---------- */
function fmtDdmmyyyyTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  } catch { return iso.slice(0, 16).replace("T", " "); }
}

function RequestsTab({ onAssign }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/bag-requests").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const act = async (rid, action) => {
    try {
      await api.post(`/bag-requests/${rid}/action`, { action });
      toast.success(`Request ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const colourSwatch = (v) => ({
    yellow: "#F5C518", blue: "#1E5FA0", red: "#C0392B", white: "#F4F4F4",
    black: "#1A1A1A", grey: "#7A7A7A",
  })[v];
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
      <table className="min-w-full text-sm" data-testid="qr-requests-table">
        <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
          <tr>
            <th className="px-4 py-2.5 text-left">Request · Date</th>
            <th className="px-4 py-2.5 text-left">Customer</th>
            <th className="px-4 py-2.5 text-left">Category · Colour</th>
            <th className="px-4 py-2.5 text-right">Qty</th>
            <th className="px-4 py-2.5 text-right">Total</th>
            <th className="px-4 py-2.5 text-left">Payment</th>
            <th className="px-4 py-2.5 text-left">PIN</th>
            <th className="px-4 py-2.5 text-left">Status</th>
            <th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F7F6F2]">
          {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-[#8A8780]">No bag requests yet</td></tr>}
          {rows.map(r => {
            const paid = r.payment_status === "paid";
            return (
            <tr key={r.id} data-testid={`qr-req-${r.id}`}>
              <td className="px-4 py-3">
                <div className="font-mono text-xs text-[#2D5A27]" data-testid={`qr-req-no-${r.id}`}>
                  {r.request_no || r.id.slice(0, 8)}
                </div>
                <div className="text-[10px] text-[#8A8780] font-mono">
                  {fmtDdmmyyyyTime(r.created_at)}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="font-medium">{r.customer_name || "—"}</div>
                <div className="text-[10px] text-[#8A8780] font-mono">{r.customer_code} · {r.customer_phone}</div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] px-2 py-0.5 bg-[#D4E0D1] text-[#2D5A27] rounded">{r.waste_category}</span>
                  {r.colour_code && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#5C5A55]">
                      <span className="w-3 h-3 rounded-full border border-[#00000020]" style={{background: colourSwatch(r.colour_code)}}/>
                      {r.colour_code}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-right font-mono">{r.quantity}</td>
              <td className="px-4 py-3 text-right font-mono">{r.total_amount ? `₹${r.total_amount.toFixed(2)}` : "—"}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {r.payment_mode && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#F7F6F2] text-[#5C5A55]">
                      {r.payment_mode === "online" ? "ONLINE" : "COD"}
                    </span>
                  )}
                  {r.payment_mode && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${paid ? "bg-[#C8E6C9] text-[#1B5E20]" : "bg-[#F9E4B7] text-[#7A5A11]"}`}>
                      {paid ? "PAID" : "UNPAID"}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                {r.delivery_pin ? (
                  <span data-testid={`qr-req-pin-${r.id}`}
                        className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#F9F0D6] border border-[#F5C518]/60 text-[#7A5A11] tracking-widest">
                    {r.delivery_pin}
                  </span>
                ) : <span className="text-[10px] text-[#8A8780]">—</span>}
              </td>
              <td className="px-4 py-3">
                <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
                  r.status === "pending" ? "bg-[#F9E4B7] text-[#7A5A11]" :
                  r.status === "approved" ? "bg-[#D4E0D1] text-[#2D5A27]" :
                  r.status === "fulfilled" ? "bg-[#C8E6C9] text-[#1B5E20]" :
                  "bg-[#F4C7B9] text-[#7A2E18]"
                }`}>{r.status}</span>
              </td>
              <td className="px-4 py-3 text-right">
                {r.status === "pending" && (
                  <>
                    <button onClick={() => act(r.id, "approve")} data-testid={`qr-req-approve-${r.id}`}
                            className="text-[#2D5A27] text-xs mr-3">Approve</button>
                    <button onClick={() => act(r.id, "reject")} data-testid={`qr-req-reject-${r.id}`}
                            className="text-[#9F1239] text-xs">Reject</button>
                  </>
                )}
                {r.status === "approved" && (
                  <button onClick={() => onAssign?.(r)} data-testid={`qr-req-assign-${r.id}`}
                          className="h-8 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs rounded inline-flex items-center gap-1">
                    <UserPlus size={11}/>Assign Bags
                  </button>
                )}
                {r.status === "fulfilled" && r.assigned_first_code && (
                  <span className="text-[10px] text-[#5C5A55] font-mono">
                    {r.assigned_first_code}–{r.assigned_last_code}
                  </span>
                )}
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- 6. Challans ---------- */
const CHALLAN_STATUS_STYLE = {
  draft: "bg-[#F9E4B7] text-[#7A5A11]",
  assigned: "bg-[#F9E4B7] text-[#7A5A11]",
  out_for_delivery: "bg-[#DDE9F6] text-[#0D47A1]",
  delivered: "bg-[#D4E0D1] text-[#2D5A27]",
};
const CHALLAN_STATUS_LABEL = {
  draft: "ASSIGNED",  // legacy fallback — treat as assigned
  assigned: "ASSIGNED",
  out_for_delivery: "OUT FOR DELIVERY",
  delivered: "DELIVERED",
};

function ChallansTab({ onDone }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null); // {id, delivery_date, assigned_staff_id, notes}
  const load = () => api.get("/delivery-challans").then(r => setRows(r.data || []));
  useEffect(() => {
    load();
    // Iter 59 — field staff live in the `field_staff` collection and are
    // exposed via GET /api/staff (not /api/users which is admin users).
    api.get("/staff?status=active")
      .then(r => setStaff(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);
  const sendWa = async (cid, mode) => {
    try {
      const r = await api.post(`/delivery-challans/${cid}/whatsapp`, { mode });
      if (mode === "manual" && r.data.url) window.open(r.data.url, "_blank");
      toast.success(mode === "api" ? "Sent via WhatsApp API" : "WhatsApp opened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const regenPin = async (cid, challan_no) => {
    if (!window.confirm(
      `Regenerate delivery PIN for ${challan_no}?\n\n` +
      "The old PIN will stop working immediately. Share the new PIN with the customer.",
    )) return;
    try {
      const r = await api.post(`/delivery-challans/${cid}/regenerate-pin`);
      toast.success(`New PIN: ${r.data.delivery_pin}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/delivery-challans/${editing.id}`, {
        delivery_date: editing.delivery_date || null,
        assigned_staff_id: editing.assigned_staff_id || null,
        notes: editing.notes || null,
      });
      toast.success("Challan updated");
      setEditing(null);
      load();
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#5C5A55]">
          Delivery challans for assigned bag distribution. Auto-created
          on each customer assignment. Flow:
          <span className="font-mono text-[#7A5A11]"> ASSIGNED</span> →
          <span className="font-mono text-[#0D47A1]"> OUT FOR DELIVERY</span> →
          <span className="font-mono text-[#2D5A27]"> DELIVERED</span>.
        </div>
        <button onClick={() => setOpen(true)} data-testid="qr-challan-new"
                className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-xs inline-flex items-center gap-1.5">
          <Plus size={12}/>New Challan
        </button>
      </div>
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <table className="min-w-full text-sm" data-testid="qr-challans-table">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
            <tr>
              <th className="px-4 py-2.5 text-left">No.</th>
              <th className="px-4 py-2.5 text-left">Customer</th>
              <th className="px-4 py-2.5 text-left">Range</th>
              <th className="px-4 py-2.5 text-right">Count</th>
              <th className="px-4 py-2.5 text-right">Total</th>
              <th className="px-4 py-2.5 text-left">Date</th>
              <th className="px-4 py-2.5 text-left">Staff</th>
              <th className="px-4 py-2.5 text-left">PIN</th>
              <th className="px-4 py-2.5 text-left">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F7F6F2]">
            {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-6 text-center text-[#8A8780]">No challans yet</td></tr>}
            {rows.map(c => {
              const staffName = staff.find(s => s.id === c.assigned_staff_id)?.name;
              const isEditing = editing?.id === c.id;
              const st = c.status || "assigned";
              return (
                <React.Fragment key={c.id}>
                  <tr data-testid={`qr-challan-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{c.challan_no}</td>
                    <td className="px-4 py-3">{c.customer_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{c.first_code} → {c.last_code}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.count}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{c.total_cost}</td>
                    <td className="px-4 py-3 text-xs">{c.delivery_date || <span className="text-[#C25D38]">—</span>}</td>
                    <td className="px-4 py-3 text-xs">{staffName || <span className="text-[#C25D38]">—</span>}</td>
                    <td className="px-4 py-3">
                      {c.delivery_pin && st !== "delivered" ? (
                        <div className="flex items-center gap-1.5">
                          <span data-testid={`qr-challan-pin-${c.id}`}
                                className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#F9F0D6] border border-[#F5C518]/60 text-[#7A5A11] tracking-widest">
                            {c.delivery_pin}
                          </span>
                          <button onClick={() => regenPin(c.id, c.challan_no)}
                                  data-testid={`qr-challan-pin-regen-${c.id}`}
                                  title="Regenerate PIN"
                                  className="text-[10px] text-[#5C5A55] hover:text-[#1A1A1A] underline">
                            regen
                          </button>
                        </div>
                      ) : <span className="text-[10px] text-[#8A8780]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
                        CHALLAN_STATUS_STYLE[st] || "bg-[#F7F6F2] text-[#5C5A55]"
                      }`} data-testid={`qr-challan-status-${c.id}`}>
                        {CHALLAN_STATUS_LABEL[st] || st}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing({
                        id: c.id, delivery_date: c.delivery_date || "",
                        assigned_staff_id: c.assigned_staff_id || "",
                        notes: c.notes || "",
                      })} data-testid={`qr-challan-edit-${c.id}`}
                              className="text-[#5C5A55] text-xs mr-3 hover:text-[#1A1A1A]">Edit</button>
                      {st === "delivered" && (
                        <a href={`${API_BASE}/delivery-challans/${c.id}/delivery-report`}
                            target="_blank" rel="noreferrer"
                            data-testid={`qr-challan-delivery-report-${c.id}`}
                            title="Detailed delivery report with GPS trail"
                            className="text-[#0D47A1] text-xs inline-flex items-center gap-1 mr-3">
                          <Database size={12} weight="fill"/>Report
                        </a>
                      )}
                      <a href={`${API_BASE}/delivery-challans/${c.id}/pdf`} target="_blank" rel="noreferrer"
                         data-testid={`qr-challan-pdf-${c.id}`} className="text-[#2D5A27] text-xs inline-flex items-center gap-1 mr-3">
                        <Download size={12}/>PDF
                      </a>
                      <button onClick={() => sendWa(c.id, "api")} data-testid={`qr-challan-wa-api-${c.id}`}
                              className="text-[#25D366] text-xs inline-flex items-center gap-1 mr-3">
                        <WhatsappLogo size={12} weight="fill"/>API
                      </button>
                      <button onClick={() => sendWa(c.id, "manual")} data-testid={`qr-challan-wa-manual-${c.id}`}
                              className="text-[#25D366] text-xs inline-flex items-center gap-1">
                        <WhatsappLogo size={12}/>Open
                      </button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="bg-[#FAFAF7]" data-testid={`qr-challan-edit-row-${c.id}`}>
                      <td colSpan={10} className="px-4 py-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Delivery Date</div>
                            <input type="date" value={editing.delivery_date}
                                   onChange={e => setEditing(s => ({...s, delivery_date: e.target.value}))}
                                   data-testid={`qr-challan-edit-date-${c.id}`}
                                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Assigned Staff</div>
                            <select value={editing.assigned_staff_id}
                                    onChange={e => setEditing(s => ({...s, assigned_staff_id: e.target.value}))}
                                    data-testid={`qr-challan-edit-staff-${c.id}`}
                                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm">
                              <option value="">— Choose field staff —</option>
                              {staff.map(u => <option key={u.id} value={u.id}>{u.name}{u.staff_code ? ` (${u.staff_code})` : ""}{u.phone ? ` · ${u.phone}` : ""}</option>)}
                            </select>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">Notes</div>
                            <input value={editing.notes}
                                   onChange={e => setEditing(s => ({...s, notes: e.target.value}))}
                                   data-testid={`qr-challan-edit-notes-${c.id}`}
                                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="text-[10px] text-[#8A8780]">
                            Setting staff + date flips this challan to <strong className="text-[#0D47A1]">OUT FOR DELIVERY</strong>. The field staff will enter the customer&apos;s 6-digit PIN to complete.
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditing(null)} data-testid={`qr-challan-edit-cancel-${c.id}`}
                                    className="h-9 px-3 border border-[#E5E2D9] rounded text-xs">Cancel</button>
                            <button onClick={saveEdit} data-testid={`qr-challan-edit-save-${c.id}`}
                                    className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-xs">Save</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && <ChallanModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); onDone?.(); }}/>}
    </div>
  );
}

function ChallanModal({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bags, setBags] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [f, setF] = useState({ customer_id: "", delivery_date: new Date().toISOString().slice(0,10),
                                  assigned_staff_id: "", notes: "" });
  useEffect(() => {
    api.get("/customers?limit=500").then(r => setCustomers(r.data || []));
    // Iter 59 — same fix: pull from /api/staff (field_staff collection).
    api.get("/staff?status=active").then(r => setStaff(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!f.customer_id) { setBags([]); return; }
    api.get(`/qr?customer_id=${f.customer_id}&status=assigned&limit=500`)
      .then(r => { setBags(r.data || []); setPicked(new Set()); })
      .catch(() => {});
  }, [f.customer_id]);
  const togglePick = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (picked.size === 0) { toast.error("Pick at least one bag"); return; }
    try {
      await api.post("/delivery-challans", {
        ...f, bag_ids: Array.from(picked),
        assigned_staff_id: f.assigned_staff_id || null,
      });
      toast.success("Challan created");
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qr-challan-modal">
        <div className="p-5 border-b border-[#E5E2D9] flex items-center justify-between">
          <div className="font-heading text-lg">New Delivery Challan</div>
          <button type="button" onClick={onClose} className="text-[#8A8780] hover:text-[#1A1A1A]"><Trash size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Customer">
            <select required value={f.customer_id} onChange={e => setF(s => ({...s, customer_id: e.target.value}))}
                    data-testid="ch-customer" className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm">
              <option value="">— Select customer —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.customer_code}</option>)}
            </select>
          </Field>
          {f.customer_id && (
            <Field label={`Pick assigned bags (${picked.size}/${bags.length})`}>
              {bags.length === 0
                ? <div className="text-xs text-[#C25D38]">No assigned bags found for this customer. Assign some first under "Customer Assign".</div>
                : (
                  <div className="border border-[#E5E2D9] rounded p-2 max-h-40 overflow-y-auto bg-[#FAFAF7]">
                    <button type="button" onClick={() => setPicked(new Set(bags.map(b => b.id)))}
                            data-testid="ch-pick-all" className="text-xs text-[#2D5A27] mb-2">Select all</button>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-1">
                      {bags.map(b => (
                        <label key={b.id} className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-mono cursor-pointer ${picked.has(b.id) ? "bg-[#D4E0D1] text-[#2D5A27]" : "bg-white border border-[#E5E2D9]"}`}>
                          <input type="checkbox" checked={picked.has(b.id)} onChange={() => togglePick(b.id)}
                                 className="accent-[#2D5A27]"/>
                          {b.qr_code}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Delivery Date">
              <input type="date" required value={f.delivery_date} onChange={e => setF(s => ({...s, delivery_date: e.target.value}))}
                     data-testid="ch-date" className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm"/>
            </Field>
            <Field label="Assigned Staff (optional)">
              <select value={f.assigned_staff_id} onChange={e => setF(s => ({...s, assigned_staff_id: e.target.value}))}
                      data-testid="ch-staff" className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm">
                <option value="">— None —</option>
                {staff.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea rows={2} value={f.notes} onChange={e => setF(s => ({...s, notes: e.target.value}))}
                      data-testid="ch-notes" className="w-full px-3 py-2 border border-[#E5E2D9] rounded text-sm"/>
          </Field>
        </div>
        <div className="p-5 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded text-sm">Cancel</button>
          <button type="submit" data-testid="ch-submit"
                  className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-sm font-medium inline-flex items-center gap-2">
            <FileText size={14}/>Create Challan
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- 7. Tracking ---------- */
function TrackingTab({ onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/qr-bags/scan-collected", { qr_code: code });
      setResult(r.data);
      toast.success(`${code} marked as collected`);
      setCode("");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="max-w-xl bg-white border border-[#E5E2D9] rounded-md p-5 space-y-4" data-testid="qr-track-form">
      <Field label="Scan QR Code (or enter manually)">
        <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} required
               data-testid="qr-track-code" placeholder="RB000001" autoFocus
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded text-sm font-mono"/>
      </Field>
      <button type="submit" disabled={busy} data-testid="qr-track-submit"
              className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded text-sm font-medium inline-flex items-center gap-2">
        <Scan size={14}/>Mark Collected
      </button>
      {result && (
        <div className="bg-[#EFF3EE] border border-[#D4E0D1] rounded p-3 text-xs" data-testid="qr-track-result">
          ✓ <span className="font-mono font-semibold">{result.qr_code}</span> → <span className="text-[#2D5A27]">{result.new_status}</span>
        </div>
      )}
    </form>
  );
}

/* ---------- shared ---------- */
const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">{label}</label>
    {children}
  </div>
);

/* ---------- 8. Audit Report ---------- */
// Iter 66 — tenant-wide QR bag audit report with detailed PDF export.
function AuditTab() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    api.get(`/qr-audit-report?${params}`).then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const downloadPdf = () => {
    const params = new URLSearchParams();
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/qr-audit-report/pdf?${params}`, "_blank");
  };
  const colourSwatch = (v) => ({
    yellow: "#F5C518", blue: "#1E5FA0", red: "#C0392B", white: "#F4F4F4",
    black: "#1A1A1A", grey: "#7A7A7A",
  }[v] || "#B0B0B0");
  return (
    <div className="space-y-4" data-testid="qr-audit-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">From</label>
          <input type="date" value={range.from} onChange={e => setRange(s => ({...s, from: e.target.value}))}
                 data-testid="qr-audit-from" className="block h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">To</label>
          <input type="date" value={range.to} onChange={e => setRange(s => ({...s, to: e.target.value}))}
                 data-testid="qr-audit-to" className="block h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <button onClick={load} disabled={loading} data-testid="qr-audit-apply"
                className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-xs">
          {loading ? "Loading…" : "Apply"}
        </button>
        <button onClick={downloadPdf} data-testid="qr-audit-download"
                className="h-9 px-3 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] rounded text-xs">
          Download detailed PDF
        </button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Lifecycle */}
            <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Bag lifecycle</div>
              <div className="space-y-1 mt-2">
                {Object.entries(data.bag_lifecycle || {}).map(([s, n]) => (
                  <div key={s} className="flex justify-between text-sm">
                    <span className="uppercase text-[10px] tracking-wider text-[#5C5A55]">{s}</span>
                    <span className="font-mono font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Challans */}
            <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Challans by status</div>
              <div className="space-y-1 mt-2">
                {Object.entries(data.challans_by_status || {}).map(([s, d]) => (
                  <div key={s} className="flex justify-between text-sm">
                    <span className="uppercase text-[10px] tracking-wider text-[#5C5A55]">{s}</span>
                    <span className="font-mono">{d.count} · ₹{d.value.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Finance */}
            <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Finance · bag invoices</div>
              <div className="space-y-1 mt-2">
                {Object.entries(data.finance || {}).map(([s, d]) => (
                  <div key={s} className="flex justify-between text-sm">
                    <span className="uppercase text-[10px] tracking-wider text-[#5C5A55]">{s}</span>
                    <span className="font-mono">{d.count} · ₹{d.amount.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Category matrix */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Stock by waste category · CPCB colours</div>
            <div className="mt-2 space-y-2">
              {Object.entries(data.by_category || {}).map(([cat, info]) => (
                <div key={cat} className="border-t border-[#F7F6F2] pt-2 flex flex-wrap items-center gap-3">
                  <div className="min-w-[8rem]">
                    <div className="font-semibold">{cat}</div>
                    <div className="text-[11px] text-[#5C5A55]">
                      {info.batches} batches · {info.bags} bags · ₹{info.value.toFixed(0)}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(info.by_colour || {}).map(([col, n]) => (
                      <span key={col} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#F7F6F2]">
                        <span className="w-3 h-3 rounded-full border border-[#00000020]" style={{background: colourSwatch(col)}}/>
                        {col} · {n}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Custody + top holders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Chain of custody events</div>
              <div className="font-heading text-3xl mt-2 text-[#2D5A27]">
                {(data.custody?.total_events || 0).toLocaleString()}
              </div>
              <div className="text-[11px] text-[#5C5A55]">
                Manual entries: {data.custody?.manual_events || 0} ({data.custody?.manual_pct || 0} %)
              </div>
            </div>
            <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Top holders (bags with customer)</div>
              <div className="mt-2 space-y-1">
                {(data.top_holders || []).map(h => (
                  <div key={h.customer_id} className="flex justify-between text-sm">
                    <span>{h.name} <span className="text-[10px] text-[#8A8780] font-mono">{h.customer_code}</span></span>
                    <span className="font-mono font-semibold text-[#2D5A27]">{h.bags_held} bags</span>
                  </div>
                ))}
                {(data.top_holders || []).length === 0 && <div className="text-[11px] text-[#8A8780]">No customer holdings recorded.</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
